import { asArray, asOptionalString, asRecord, asString } from '../../dto/common';
import { formatUnit } from '../../utils/units';

/**
 * Operator-facing "why is this unavailable" for PZ/WZ documents.
 *
 * The backend computes the rule once (services/warehouseBlockedActionsService.js)
 * and ships it on every document GET as `blockedActions`; the same code and
 * `relatedDocuments` come back on a rejected mutation. This module is the
 * single place that turns either into a Polish sentence, so the tooltip on a
 * disabled button, the alert on the document card and the toast after a
 * rejected call read identically.
 *
 * Wording lives in `pz:error.*` / `wz:error.*` (reason, shared with reactive
 * toasts) plus `pz:blocked.remedy.*` / `wz:blocked.remedy.*` (what to do
 * instead). Related documents name only document numbers, batches and
 * physical quantities — the backend never puts money in them.
 */
export type WarehouseRelatedDocumentType = 'WZ' | 'PZ' | 'ORDER' | 'ITEM' | 'TRANSPORT' | 'INVOICE';

export interface WarehouseRelatedDocument {
  type: WarehouseRelatedDocumentType;
  id: string | null;
  number: string | null;
  batchNumber: string | null;
  quantity: number | null;
  unit: string | null;
}

export interface WarehouseBlockedAction {
  action: string;
  code: string;
  relatedDocuments: WarehouseRelatedDocument[];
  /** Advisory only — the action still works (e.g. DELIVERED without a signed WZ). */
  warning?: boolean;
}

/**
 * i18n namespace that carries the `blocked.*` keys. Customer orders and
 * transports joined the contract on 2026-09-18 (owner decision) and reuse
 * this module unchanged.
 */
export type WarehouseDocumentNamespace = 'pz' | 'wz' | 'orders' | 'transport';

const RELATED_TYPES: WarehouseRelatedDocumentType[] = ['WZ', 'PZ', 'ORDER', 'ITEM', 'TRANSPORT', 'INVOICE'];

export function normalizeRelatedDocuments(raw: unknown): WarehouseRelatedDocument[] {
  return asArray(raw, (item) => {
    const row = asRecord(item);
    const type = asOptionalString(row?.type);
    const quantity = Number(row?.quantity);
    return {
      type: type && RELATED_TYPES.includes(type as WarehouseRelatedDocumentType)
        ? (type as WarehouseRelatedDocumentType)
        : 'ITEM',
      id: asOptionalString(row?.id) ?? null,
      number: asOptionalString(row?.number) ?? null,
      batchNumber: asOptionalString(row?.batchNumber) ?? null,
      quantity: row?.quantity == null || !Number.isFinite(quantity) ? null : quantity,
      unit: asOptionalString(row?.unit) ?? null,
    };
  });
}

/** Missing or malformed payload means "nothing known", never "nothing blocked". */
export function normalizeBlockedActions(raw: unknown): WarehouseBlockedAction[] {
  return asArray(raw, (item) => {
    const row = asRecord(item);
    return {
      action: asString(row?.action),
      code: asString(row?.code),
      relatedDocuments: normalizeRelatedDocuments(row?.relatedDocuments),
      ...(row?.warning === true ? { warning: true } : {}),
    };
  }).filter((row) => row.action && row.code);
}

export function findBlockedAction(
  list: WarehouseBlockedAction[] | undefined,
  action: string,
): WarehouseBlockedAction | undefined {
  return (list || []).find((row) => row.action === action);
}

const quantityFormatter = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3 });

function formatQuantity(doc: WarehouseRelatedDocument): string {
  if (doc.quantity == null) return '';
  return `${quantityFormatter.format(doc.quantity)} ${formatUnit(doc.unit)}`;
}

/**
 * "partia PZ/06/08/2026-001 została wydana na WZ/08/08/2026 (250 szt.)" — one
 * clause per related document, joined with commas. Empty string when nothing
 * is attached, so callers can concatenate without a guard.
 */
export function formatRelatedDocuments(
  docs: WarehouseRelatedDocument[] | undefined,
  t: any,
  ns: WarehouseDocumentNamespace,
): string {
  const clauses = (docs || []).map((doc) => {
    const params = {
      number: doc.number || '—',
      batchNumber: doc.batchNumber || '—',
      quantity: formatQuantity(doc),
    };
    switch (doc.type) {
      case 'WZ':
        return t(`${ns}:blocked.related.issuedOnWZ`, params);
      case 'ORDER':
        return t(`${ns}:blocked.related.reservedForOrder`, params);
      case 'PZ':
        return t(`${ns}:blocked.related.consumedFromPZ`, params);
      case 'TRANSPORT':
        return t(`${ns}:blocked.related.transport`, params);
      case 'INVOICE':
        return t(`${ns}:blocked.related.invoiced`, params);
      default:
        return t(`${ns}:blocked.related.item`, params);
    }
  }).filter(Boolean);
  return clauses.join(', ');
}

export interface BlockedActionMessageInput {
  code: string;
  relatedDocuments?: WarehouseRelatedDocument[];
}

/**
 * Full operator sentence: reason, then the documents behind it, then what to
 * do instead. `reasonKeys` is the namespace's code→key map (exported by
 * `pzErrorMessages.ts` / `wzErrorMessages.ts`); an unmapped code falls back to
 * the generic unavailable sentence rather than leaking the code.
 */
export function getBlockedActionMessage(
  blocked: BlockedActionMessageInput,
  t: any,
  ns: WarehouseDocumentNamespace,
  reasonKeys: Record<string, string>,
): string {
  const reasonKey = reasonKeys[blocked.code];
  const reason = reasonKey ? t(reasonKey) : t(`${ns}:blocked.unavailable`);
  const related = formatRelatedDocuments(blocked.relatedDocuments, t, ns);
  const remedyKey = `${ns}:blocked.remedy.${blocked.code}`;
  const remedy = t(remedyKey);
  const parts = [reason];
  if (related) parts.push(t(`${ns}:blocked.related.prefix`, { list: related }));
  if (remedy && remedy !== remedyKey) parts.push(remedy);
  return parts.join(' ');
}
